import { useCallback, useEffect, useRef, useState } from 'react';

import { QTextarea } from '@qualio/ui-components';

type BufferedQTextareaProps = React.ComponentProps<typeof QTextarea> & {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
};
/**
 * A wrapper around QTextArea that buffers the input value internally to prevent
 * cursor-jumping issues caused by React 18's concurrent rendering.
 *
 * In React 18 with createRoot, Chakra UI v1's FormControl triggers async
 * re-renders on focus, which can reset the cursor position when the controlled
 * value prop is re-applied to the DOM input. This wrapper maintains its own
 * state and only syncs with the external value when the input is not focused.
 */

export const BufferedQTextarea: React.FC<BufferedQTextareaProps> = ({
  value = '',
  onChange,
  onBlur,
  onFocus,
  ...rest
}) => {
  const [localValue, setLocalValue] = useState(value);
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setLocalValue(value);
    }
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setLocalValue(e.target.value);
      onChange?.(e);
    },
    [onChange],
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      isFocusedRef.current = true;
      onFocus?.(e);
    },
    [onFocus],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      isFocusedRef.current = false;
      setLocalValue(value);
      onBlur?.(e);
    },
    [onBlur, value],
  );

  return <QTextarea {...rest} value={localValue} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} />;
};

export default BufferedQTextarea;
